import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { resolveMediaUrl } from "@/lib/media";

/**
 * Cinematic scroll-expansion hero.
 *
 * Layers (back → front), all inside a viewport pinned beneath the sticky header
 * (the header publishes its real height as `--header-h`, so the pinned stage also
 * works on mobile where the header is taller than 64px) while the user scrolls
 * through a taller section:
 *   1. Admin-managed background IMAGE (full-bleed, visually fixed during the pin)
 *   2. Scrim gradient (darkens the image for copy legibility)
 *   3. Centered VIDEO frame that expands to fill the pinned viewport
 *   4. Headline overlay (the single line of copy in the hero)
 *   5. "SCROLL TO EXPLORE" indicator
 *
 * The effect uses NATIVE scroll only — no wheel/touch `preventDefault`, no scroll
 * lock, no forced `scrollTo`. Progress is the actual scroll distance (reversible),
 * applied to refs inside requestAnimationFrame, so there are zero React re-renders
 * while scrolling.
 *
 * The VIDEO is a fixed frontend build asset (module constant, no prop, no admin
 * surface). The BACKGROUND IMAGE is the admin-managed hero image via `bgImageSrc`.
 */

const HERO_VIDEO = "/media/hero/hero-cinematic.mp4";
const DEFAULT_HERO_IMAGE = "/media/hero/hero-default.jpg";

const clamp01 = (x) => Math.min(1, Math.max(0, x));

export default function CinematicHero({
  bgImageSrc,
  alt = "Shree Shyam Gadgets",
  title = "Upgrade Your Everyday.",
  scrollHint = "SCROLL TO EXPLORE",
}) {
  const sectionRef = useRef(null);
  const stageRef = useRef(null);
  const frameRef = useRef(null);
  const shadowRef = useRef(null);
  const scrimRef = useRef(null);
  const copyRef = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);
  const indicatorRef = useRef(null);

  const [reducedMotion, setReducedMotion] = useState(false);
  const [slowNetwork, setSlowNetwork] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  const bg = bgImageSrc ? resolveMediaUrl(bgImageSrc) : DEFAULT_HERO_IMAGE;

  // Split the headline into two balanced lines for the slide-apart treatment while
  // keeping the full sentence in a single <h1> for SEO/screen readers.
  const words = title.split(" ").filter(Boolean);
  const mid = Math.ceil(words.length / 2);
  const line1 = words.slice(0, mid).join(" ");
  const line2 = words.slice(mid).join(" ");

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn) {
      const update = () =>
        setSlowNetwork(conn.saveData === true || conn.effectiveType === "2g" || conn.effectiveType === "3g");
      update();
    }
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Scroll-linked progress. Reduced-motion renders a static hero and never mounts
  // this listener.
  useEffect(() => {
    if (reducedMotion) return undefined;
    let raf = 0;

    const apply = (p) => {
      const stage = stageRef.current;
      const frame = frameRef.current;
      const shadow = shadowRef.current;
      const scrim = scrimRef.current;
      const copy = copyRef.current;
      const line1El = line1Ref.current;
      const line2El = line2Ref.current;
      const indicator = indicatorRef.current;
      if (!stage) return;

      const endW = stage.offsetWidth;
      const endH = stage.offsetHeight;
      const isMobile = endW < 768;
      const startW = (isMobile ? 0.82 : 0.58) * endW;
      const startH = (isMobile ? 0.46 : 0.62) * endH;

      if (frame) {
        frame.style.width = `${startW + (endW - startW) * p}px`;
        frame.style.height = `${startH + (endH - startH) * p}px`;
        frame.style.borderRadius = `${28 * (1 - clamp01(p / 0.85))}px`;
      }
      if (shadow) shadow.style.opacity = String(1 - clamp01(p / 0.55));
      if (scrim) scrim.style.opacity = String(0.45 - 0.35 * p);

      const copyOpacity = 1 - clamp01((p - 0.35) / 0.3);
      if (copy) {
        copy.style.opacity = String(copyOpacity);
        copy.style.transform = `translateY(${-p * 64}px)`;
        copy.style.pointerEvents = copyOpacity > 0.05 ? "auto" : "none";
      }
      if (line1El) line1El.style.transform = `translateX(${-p * 6}vw)`;
      if (line2El) line2El.style.transform = `translateX(${p * 6}vw)`;
      if (indicator) indicator.style.opacity = String(1 - clamp01(p / 0.15));
    };

    const update = () => {
      raf = 0;
      const section = sectionRef.current;
      const stage = stageRef.current;
      if (!section || !stage) return;
      const top = section.getBoundingClientRect().top + window.scrollY;
      const maxScroll = Math.max(1, section.offsetHeight - stage.offsetHeight);
      apply(clamp01((window.scrollY - top) / maxScroll));
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update(); // set the initial (p = 0) geometry before paint
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reducedMotion]);

  const handleBgError = (e) => {
    const img = e?.currentTarget;
    if (!img || img.dataset.heroFallback === "1") return;
    img.dataset.heroFallback = "1";
    img.src = DEFAULT_HERO_IMAGE;
  };

  const showVideo = !reducedMotion && !slowNetwork && !videoFailed;

  // Reduced motion: static, readable, no pinning, no video.
  if (reducedMotion) {
    return (
      <section className="relative h-[100svh] min-h-[560px] overflow-hidden bg-slate-900 mt-[calc(-1*var(--header-h,4rem))]" data-testid="cinematic-hero">
        <img src={bg} alt={alt} onError={handleBgError} data-testid="hero-background"
          className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-900/50 to-slate-900/40" />
        <div className="relative h-full container-x flex flex-col items-center justify-center pb-[8vh] text-center text-white">
          <h1 className="text-[2.1rem] sm:text-[3.35rem] lg:text-[4.2rem] font-bold tracking-tight font-display leading-[1.05] max-w-3xl"
            style={{ textShadow: "0 1px 2px rgba(2,6,23,0.45), 0 14px 44px rgba(2,6,23,0.5)" }}
            aria-label={title} data-testid="hero-title">
            {title}
          </h1>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className="relative h-[calc(100svh+110vh)] md:h-[calc(100svh+140vh)] mt-[calc(-1*var(--header-h,4rem))]"
      data-testid="cinematic-hero"
    >
      <div ref={stageRef} data-testid="hero-stage" className="sticky top-0 h-[100svh] overflow-hidden bg-slate-900">
        {/* 1. Admin-managed background image (visually fixed during the pin) */}
        <img src={bg} alt={alt} onError={handleBgError} data-testid="hero-background"
          className="absolute inset-0 h-full w-full object-cover" />

        {/* 2. Scrim (density fades as the video expands) */}
        <div ref={scrimRef} className="absolute inset-0 bg-slate-950 pointer-events-none" style={{ opacity: 0.45 }} />

        {/* 3. Expanding video frame */}
        <div
          ref={frameRef}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden w-[82vw] h-[46vh] md:w-[58vw] md:h-[62vh]"
          style={{ borderRadius: 28, zIndex: 20 }}
        >
          <img src={bg} alt="" aria-hidden onError={handleBgError} className="absolute inset-0 h-full w-full object-cover" />
          {showVideo && (
            <video
              src={HERO_VIDEO}
              poster={bg}
              muted
              loop
              playsInline
              autoPlay
              preload="metadata"
              disablePictureInPicture
              disableRemotePlayback
              aria-hidden="true"
              tabIndex={-1}
              onCanPlay={() => setVideoReady(true)}
              onError={() => setVideoFailed(true)}
              data-testid="hero-video"
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${videoReady ? "opacity-100" : "opacity-0"}`}
            />
          )}
          {/* Soft shadow that dissolves as the frame grows */}
          <div ref={shadowRef} className="absolute inset-0 pointer-events-none" style={{ boxShadow: "0 40px 80px -20px rgba(15,23,42,0.55)" }} />
          {/* Hairline ring */}
          <div className="absolute inset-0 ring-1 ring-white/30 pointer-events-none" />
        </div>

        {/* 4. Headline overlay (upper-middle of the frame, nothing else) */}
        <div ref={copyRef} className="absolute inset-0 z-30 flex items-center justify-center pb-[10vh] md:pb-[14vh] text-center text-white pointer-events-none">
          <div className="px-6 max-w-4xl">
            <h1 className="text-[2.1rem] sm:text-[3.35rem] lg:text-[4.2rem] font-bold tracking-tight font-display leading-[1.05]"
              style={{ textShadow: "0 1px 2px rgba(2,6,23,0.45), 0 14px 44px rgba(2,6,23,0.5)" }}
              aria-label={title}
              data-testid="hero-title">
              <span ref={line1Ref} className="block will-change-transform">{line1}</span>
              <span ref={line2Ref} className="block will-change-transform">{line2}</span>
            </h1>
          </div>
        </div>

        {/* 5. Scroll indicator */}
        <div ref={indicatorRef} className="absolute bottom-[calc(4.5rem+env(safe-area-inset-bottom))] md:bottom-6 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2 text-white/80 pointer-events-none"
          data-testid="hero-scroll-indicator">
          <span className="text-[10px] font-semibold uppercase tracking-[0.3em]">{scrollHint}</span>
          <ChevronDown className="h-4 w-4 animate-bounce" />
        </div>
      </div>
    </section>
  );
}
